import React, { useEffect, useMemo, useState } from "react";
import { Form, DatePicker, FormInstance, Row, Col, Spin, Checkbox } from "antd";
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks/redux";
import {
    getLocations,
    getAirports, getEstablishmentTypes, getEstablishmentTypeQuestions
} from "../../../../redux/features/generaleWebData/generaleWebDataAsyncThunk";
import { FormWrapper } from "../../../../components/styles";
import { capitalizeWord } from "../../../../utils/helpers/capitalizeWord";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { sortOptionsAlphabetically } from "../../../../utils/helpers/sortOptionsAlphabetically";
import { disabledFutureDate } from "../../../../utils/helpers/convertUtcToLocal";
import { Question, ILocationQuestion, Report } from "../../../../types";
import Input, { TextArea } from "../../../../components/Input";
import Dropdown from "../../../../components/Dropdown";
import { Label } from "../../../../components/Input/styles";
import './style.css'
import { preserveOriginalTime } from "../../../../utils/helpers/dateFunctions";

dayjs.extend(isBetween);

type TimeOfDayKey = "morning" | "afternoon" | "evening";

const TIME_OF_DAY_CONFIG: Record<TimeOfDayKey, { label: string; hours: number; minutes: number }> = {
    morning: { label: "Morning", hours: 9, minutes: 0 },
    afternoon: { label: "Afternoon", hours: 14, minutes: 0 },
    evening: { label: "Evening", hours: 20, minutes: 0 },
};

const deriveTimeOfDayFromDate = (value?: string | Date | dayjs.Dayjs | null): TimeOfDayKey | null => {
    if (!value) {
        return null;
    }

    if (typeof value === "string" && TIME_OF_DAY_CONFIG[value as TimeOfDayKey]) {
        return value as TimeOfDayKey;
    }

    const parsed = dayjs(value);

    if (!parsed.isValid()) {
        return null;
    }

    const hour = parsed.hour();

    if (hour < 12) {
        return "morning";
    }
    if (hour < 18) {
        return "afternoon";
    }
    return "evening";
};

const applyTimeOfDayToDayjs = (date: dayjs.Dayjs, timeKey?: TimeOfDayKey | null) => {
    if (!timeKey) {
        return date;
    }

    const config = TIME_OF_DAY_CONFIG[timeKey];
    if (!config) {
        return date;
    }

    return date
        .startOf("day")
        .hour(config.hours)
        .minute(config.minutes)
        .second(0)
        .millisecond(0);
};

interface LocationDetailsFormProps {
    form: FormInstance;
    report: Report | null;
}

const LocationDetailsForm: React.FC<LocationDetailsFormProps> = ({ form, report }) => {
    const dispatch = useAppDispatch();
    const { airportState, locationState, establishmentTypeState, establishmentTypeQuestionState } = useAppSelector((state) => state.generaleWebData);
    const { airports, status: airportStatus } = useMemo(() => airportState, [airportState]);
    const { establishmentTypes, status: establishmentTypeStatus } = useMemo(() => establishmentTypeState, [establishmentTypeState]);
    const { establishmentTypeQuestions } = useMemo(() => establishmentTypeQuestionState, [establishmentTypeQuestionState]);
    const { locations, status: locationStatus } = useMemo(() => locationState, [locationState]);

    const [questionLoading, setQuestionLoading] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState<any>([]);
    const [checkedValues, setCheckedValues] = useState<{ [key: string]: boolean | string }>({});
    const selectedEstablishmentType = Form.useWatch('establishmentType', form);

    const handleSelectChange = (questionId: string, value: string) => {
        setSelectedOptions((prev: any) => ({
            ...prev,
            [questionId]: value,
        }));
    };

    const handleCheckboxChange = (questionId: string, e: any) => {
        setCheckedValues({
            ...checkedValues,
            [questionId]: e.target.checked ? "true" : "false",
        });
    };

    Form.useWatch([], form);

    useEffect(() => {
        if (airportStatus === "idle" && airports.length === 0) {
            dispatch(getAirports());
        }
    }, [airportStatus, airports, dispatch]);

    useEffect(() => {
        if (locationStatus === "idle" && locations.length === 0) {
            dispatch(getLocations());
        }
    }, [locationStatus, locations, dispatch]);

    useEffect(() => {
        if (establishmentTypeStatus === "idle" && establishmentTypes.length === 0) {
            dispatch(getEstablishmentTypes());
        }
    }, [establishmentTypeStatus, establishmentTypes, dispatch]);

    const airportsOptions = useMemo(() => {
        return airports.map((airport) => ({
            label: airport.name,
            value: airport.id,
            key: airport.id,
        }));
    }, [airports]);

    const locationsOptions = useMemo(() => {
        return locations.map((location) => ({
            label: location.name,
            value: location.id,
            key: location.id,
        }));
    }, [locations]);

    const establishmentTypeOptions = useMemo(() => {
        return establishmentTypes.map((establishmentType) => ({
            label: establishmentType.name,
            value: establishmentType.id,
            key: establishmentType.id,
        }));
    }, [establishmentTypes]);

    const getEstablishmentTypeQuestionsHandle = async (establishmentTypeId: string) => {
        setQuestionLoading(true);
        await dispatch(getEstablishmentTypeQuestions({ search_query: '', offset: 0, limit: 1000, establishmentTypeId }));
        setQuestionLoading(false);
    };

    useEffect(() => {
        if (selectedEstablishmentType) {
            getEstablishmentTypeQuestionsHandle(selectedEstablishmentType);
        }
    }, [selectedEstablishmentType]);

    const selectedLocation = Form.useWatch(["location"], form);
    const selectedLocationQuestions = useMemo(() => locations.find((location) => location.id === selectedLocation)?.questions, [selectedLocation, locations]);

    useEffect(() => {
        if (report) {
            const itemData = { ...report };

            const formData: { [key: string]: any } = {
                ...itemData,
                airport: report?.airport?.id,
                establishmentType: report?.establishmentType?.id,
                travelDate: report?.travelDate ? dayjs(report?.travelDate) : null,
                location: report?.location?.id,
                lostDate: report?.lostDate ? dayjs(report.lostDate) : null,
                lostTime: report?.lostTime || deriveTimeOfDayFromDate(report?.lostDate) || null,
            };

            itemData?.location?.questions?.forEach((question: ILocationQuestion) => {
                formData[`locationQuestion_${question.id}`] = question.answers?.answer;
            });

            itemData?.establishmentType?.questions?.forEach((question: any) => {
                if (question.fieldType === 2) {
                    formData[`establishmentTypeQuestion_${question.id}`] = question.answers?.answer === "true"; // Default to false (unchecked)
                } else {
                    formData[`establishmentTypeQuestion_${question.id}`] = question.answers?.answer;
                }
            });

            itemData?.establishmentType?.questions?.forEach((question) => {
                question?.options?.map((option) => {
                    option?.subQuestion?.map((subQuestion: any) => {
                        formData[`establishmentTypeSubQuestion_${subQuestion?.id}`] = subQuestion?.answers?.answer;
                    })
                })
            });

            form.setFieldsValue(formData);

            // Load establishment type questions if establishment type is pre-filled
            if (report?.establishmentType?.id) {
                getEstablishmentTypeQuestionsHandle(report.establishmentType.id);
            }
        }
    }, [report]);

    useEffect(() => {
        if (report && report?.establishmentType?.questions) {
            // Create an initial checked state for all questions with fieldType 2 (checkbox)
            const initialCheckedValues: { [key: string]: boolean | string } = {};
            const updatedSelectedOptions: { [key: string]: any } = {};

            for (const question of report?.establishmentType?.questions) {
                // Set default checked value to false only for fieldType 2 (checkbox)
                if (question.fieldType === 2) {
                    initialCheckedValues[question.id] = question.answers?.answer === "true"; // Default to false (unchecked)
                } else if (question.fieldType === 3) {
                    const selectedBrand = question?.options?.find(
                        (option) => option.name === question?.answers?.answer
                    );

                    if (selectedBrand) {
                        updatedSelectedOptions[question.id] = selectedBrand?.id; // Save dynamic value
                    }
                }
            }

            // Update the state once after looping
            setSelectedOptions((prev: any) => ({
                ...prev,
                ...updatedSelectedOptions,
            }));
            setCheckedValues(initialCheckedValues); // Update the state with initial checked values
        }
    }, [report]);

    const timeOfDayOptions = useMemo(() => (
        Object.entries(TIME_OF_DAY_CONFIG).map(([key, config]) => ({
            key,
            value: key,
            label: config.label,
        }))
    ), []);

    const handleDateChange = (date: dayjs.Dayjs | null) => {
        const currentTime = form.getFieldValue("lostTime") as TimeOfDayKey | null;

        if (!date) {
            form.setFieldValue("lostDate", null);
            return;
        }

        let updatedDate = date.clone();

        if (currentTime) {
            updatedDate = applyTimeOfDayToDayjs(updatedDate, currentTime);
        } else {
            updatedDate = preserveOriginalTime(updatedDate, report?.lostDate) || updatedDate;
        }

        form.setFieldValue("lostDate", updatedDate);
    };

    const handleLostTimeSelect = (timeOfDay: TimeOfDayKey) => {
        form.setFieldValue("lostTime", timeOfDay);
        const currentDate = form.getFieldValue("lostDate");

        if (currentDate) {
            const currentDateObj = dayjs(currentDate);
            const updatedDateTime = applyTimeOfDayToDayjs(currentDateObj, timeOfDay);
            form.setFieldValue("lostDate", updatedDateTime);
        }
    };

    return (
        <FormWrapper form={form} layout="vertical">
            <Row gutter={16} className="row-gap">
                <Col span={24} md={12}>
                    <Form.Item
                        label={<Label>Lost Date</Label>}
                        getValueProps={(value) => ({
                            value: value ? dayjs(value) : undefined,
                        })}
                        name="lostDate"
                        rules={[{ required: true, message: "Please enter Lost Date!" }]}
                    >
                        <DatePicker
                            style={{ width: "100%" }}
                            placeholder="Select Lost Date"
                            size="large"
                            format="MM/DD/YYYY"
                            disabledDate={disabledFutureDate}
                            onChange={handleDateChange}
                        />
                    </Form.Item>
                </Col>

                <Col span={24} md={12}>
                    <Dropdown
                        fontWeight="700"
                        label="Lost Time"
                        name="lostTime"
                        placeholder="Select Lost Time"
                        options={timeOfDayOptions}
                        rules={[{ required: true, message: "Please enter Lost Time!" }]}
                        onSelect={(value) => handleLostTimeSelect(value as TimeOfDayKey)}
                    />
                </Col>
            </Row>
            <Row gutter={16} className="row-gap">
                <Col span={24} md={24}>
                    <Dropdown
                        placeholder='Select a Establishment Type'
                        fontWeight="700"
                        label="Establishment Type"
                        name="establishmentType"
                        options={sortOptionsAlphabetically(establishmentTypeOptions, "label")}
                        loading={establishmentTypeStatus === "loading"}
                        value={form.getFieldValue("establishmentType")}
                        rules={[{ required: true, message: "Please select a EstablishmentType!" }]}
                    // onSelect={onChangeEstablishmentType}
                    />
                </Col>
            </Row>

            <Row gutter={16} className="row-gap">
                <Col span={24} md={24}>
                    {questionLoading ? (
                        <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                            <Spin />
                        </div>
                    ) : (
                        (establishmentTypeQuestions?.length > 0 || (report?.establishmentType?.questions && report.establishmentType.questions.length > 0)) && (
                            <>
                                {(establishmentTypeQuestions?.length > 0 ? establishmentTypeQuestions : report?.establishmentType?.questions || [])?.map((question) => {
                                    const fieldName = `establishmentTypeQuestion_${question.id}`;

                                    switch (question.fieldType) {
                                        case 1:
                                            return (
                                                <Input
                                                    fontWeight={question.isRequired ? "700" : "400"}
                                                    label={question.fieldName}
                                                    placeholder={question.fieldName}
                                                    name={fieldName}
                                                    rules={[{
                                                        required: question.isRequired,
                                                        message: "Please enter answer"
                                                    }]}
                                                    onChange={(e: any) => {
                                                        form.setFieldsValue({ [fieldName]: e.target.value });
                                                    }}
                                                />
                                            );
                                        case 2:
                                            return (
                                                <>
                                                    <Form.Item
                                                        name={fieldName}
                                                        valuePropName="checked"
                                                        key={question.id}
                                                        rules={[{
                                                            required: question.isRequired,
                                                            message: "Please enter answer"
                                                        }]}
                                                        initialValue={checkedValues[question.id] === "true"}
                                                    >
                                                        <Checkbox checked={checkedValues[question.id] === "true"}
                                                            style={{ fontWeight: question.isRequired ? "700" : "400" }}
                                                            onChange={(e) => handleCheckboxChange(question.id, e)}>{question.fieldName}</Checkbox>
                                                    </Form.Item>
                                                    {question?.options && question?.options.length > 0 && (
                                                        <>
                                                            {question?.options?.map((option) => (
                                                                <div style={{ width: "100%" }} key={option.id}>
                                                                    {/* <span>{checkedValues[question.id] && option.name === checkedValues[question.id] && option.name === "true" ? "Yes" : checkedValues[question.id] && option.name === checkedValues[question.id] && option.name === "false" ? "No" : ""}</span> */}
                                                                    {checkedValues[question.id] && option.name === checkedValues[question.id] && option?.subQuestion?.map((subQ: any) => (
                                                                        <TextArea
                                                                            label={subQ.fieldName}
                                                                            name={`establishmentTypeSubQuestion_${subQ.id}`}
                                                                            placeholder={subQ.placeholder}
                                                                            rules={[{
                                                                                required: subQ.isRequired,
                                                                                message: `Please enter ${subQ.fieldName}`
                                                                            }]}
                                                                            fontWeight={subQ.isRequired ? "700" : "400"}
                                                                            onChange={(e: any) => {
                                                                                form.setFieldsValue({ [`subQuestion_${subQ.id}`]: e.target.value });
                                                                            }}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            ))}
                                                        </>
                                                    )}
                                                </>
                                            );
                                        case 3:
                                            // Use dynamicOptions for questions with questionType, otherwise use static options
                                            const optionsSource = question?.dynamicOptions || question?.options || [];
                                            const questionOptions = optionsSource.map((option: any) => ({
                                                label: option.code ? `${option.name} (${option.code})` : option.name,
                                                value: option.id,
                                                key: option.id,
                                            }))
                                            const matchingSubQuestions = question?.options?.find((opt) => opt?.id === selectedOptions[question?.id])?.subQuestion || [];

                                            return (
                                                <>
                                                    <Dropdown
                                                        fontWeight={question.isRequired ? "700" : "400"}
                                                        label={question.fieldName}
                                                        name={fieldName}
                                                        options={sortOptionsAlphabetically(questionOptions, "label") || []}
                                                        rules={[{
                                                            required: question.isRequired,
                                                            message: "Please enter answer"
                                                        }]}
                                                        onSelect={(value) => handleSelectChange(question.id, value)}
                                                    />

                                                    {/* Render subQuestions dynamically if available */}
                                                    {selectedOptions[question?.id] && matchingSubQuestions?.length > 0 && matchingSubQuestions?.map((subQ: any) => (
                                                        <TextArea
                                                            label={subQ.fieldName}
                                                            name={`establishmentTypeSubQuestion_${subQ.id}`}
                                                            placeholder={subQ.placeholder}
                                                            rules={[{
                                                                required: subQ.isRequired,
                                                                message: `Please enter ${subQ.fieldName}`
                                                            }]}
                                                            fontWeight={subQ.isRequired ? "700" : "400"}
                                                            onChange={(e: any) => {
                                                                form.setFieldsValue({ [`establishmentTypeSubQuestion_${subQ.id}`]: e.target.value });
                                                            }}
                                                        />
                                                    ))}
                                                </>
                                            );
                                        default:
                                            return null;
                                    }
                                })}
                            </>
                        )
                    )}
                </Col>
            </Row>

            {selectedLocationQuestions?.map((question) => (
                <Input
                    fontWeight={question.isRequired ? "700" : "400"}
                    label={question.fieldName}
                    name={`locationQuestion_${question.id}`}
                    rules={[{ required: question.isRequired, message: "Please enter a location question!" }]}
                    placeholder={question.placeholder}
                    onChange={(e: any) => {
                        form.setFieldsValue({ [`locationQuestion_${question.id}`]: e.target.value });
                    }}
                    onBlur={(e: any) => {
                        const capitalizedValue = capitalizeWord(e.target.value);
                        form.setFieldsValue({ [`locationQuestion_${question.id}`]: capitalizedValue });
                    }}
                />
            ))}

            {/* <TextArea
                label="Additional remarks or information"
                name="whereDescription"
                placeholder="Enter additional remarks or information"
                rules={[{ required: false, message: "Please enter additional remarks or information" }]}
                fontWeight="400"
                onChange={(e: any) => {
                    form.setFieldsValue({ whereDescription: e.target.value });
                }}
            /> */}
        </FormWrapper>
    );
};

export default LocationDetailsForm;
